import { PaymentMethod } from '@/enums/PaymentMethod';
import IPaymentSplit from '@/interfaces/PaymentSplit';
import PaymentSummary from '@/interfaces/PaymentSummary';
import ResponsePoint from '@/interfaces/ResponsePoint';
import { useCartStore } from '@/stores/cart';
import { usePaymentStore } from '@/stores/payment';

export const usePayment = () => {
  const cartStore = useCartStore();
  const paymentStore = usePaymentStore();

  /**
   * 장바구니 데이터에서 결제 요약 데이터 생성
   * @returns 결제 요약 데이터
   */
  const createPaymentSummaryFromCart = (): PaymentSummary => {
    return {
      amount: cartStore.finalAmount,
      taxFree: cartStore.getCartTotalFreeAmount(),
      tax: cartStore.getCartTotalTaxAmount(),
      deposit: 0,
      preTip: 0,
      splits: [],
    };
  };

  /**
   * 결제 분할 데이터 생성. 마지막 분할인 경우 반올림 오차 조정.
   * @param method 결제 수단
   * @param amount 결제 금액. 입력 없으면 남은 금액 전체.
   * @returns 결제 분할 데이터
   */
  const createPaymentSplit = (method: PaymentMethod, amount?: number, point?: ResponsePoint): IPaymentSplit => {
    const paid = paymentStore.summary.splits.reduce((sum, s) => sum + s.amount, 0);
    const remaining = paymentStore.summary.amount - paid;

    const splitAmount = amount ?? remaining; // 입력 없으면 남은 금액 사용

    // 남은 금액을 기존 비율대로 나눔
    const ratio = paymentStore.summary.amount > 0 ? splitAmount / paymentStore.summary.amount : 0;

    const split: IPaymentSplit = {
      method,
      amount: splitAmount,
      taxFree: Math.round(paymentStore.summary.taxFree * ratio),
      tax: Math.round(paymentStore.summary.tax * ratio),
      deposit: Math.round(paymentStore.summary.deposit * ratio),
      preTip: Math.round(paymentStore.summary.preTip * ratio),
      point,
    };

    // 마지막 split일 경우, 반올림 오차 조정
    if (splitAmount > 0 && splitAmount === remaining) {
      const sums = paymentStore.summary.splits.reduce(
        (acc, s) => ({
          amount: acc.amount + s.amount,
          taxFree: acc.taxFree + s.taxFree,
          tax: acc.tax + s.tax,
          deposit: acc.deposit + s.deposit,
          preTip: acc.preTip + s.preTip,
        }),
        { amount: 0, taxFree: 0, tax: 0, deposit: 0, preTip: 0 },
      );

      split.amount = paymentStore.summary.amount - sums.amount;
      split.taxFree = paymentStore.summary.taxFree - sums.taxFree;
      split.tax = paymentStore.summary.tax - sums.tax;
      split.deposit = paymentStore.summary.deposit - sums.deposit;
      split.preTip = paymentStore.summary.preTip - sums.preTip;
    }

    return split;
  };

  return { createPaymentSummaryFromCart, createPaymentSplit };
};
